// src/pages/AiTripBuilder.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { supabase } from '../services/supabaseClient';
import { 
  FaMagic, FaMapMarkerAlt, FaCalendarDay, FaHotel, 
  FaUtensils, FaSpa, FaShoppingCart, FaArrowRight, 
  FaSync, FaCheckCircle, FaStar, FaHeart, FaLeaf, FaWineGlassAlt, FaChevronDown, FaShieldAlt, FaExternalLinkAlt
} from 'react-icons/fa';

// --- TYPES ---
type TripItem = {
  id: string;
  business_id?: string;
  type: 'hotel' | 'resto' | 'activity';
  time: string;
  title: string;
  description: string;
  price: number;
  image: string;
  icon: React.ReactNode;
};

// --- ÉTAPES DE L'IA ---
const AI_STEPS = [
  "Analyse de vos préférences...",
  "Recherche des meilleures correspondances locales...",
  "Vérification des disponibilités en temps réel...",
  "Optimisation de votre itinéraire...",
  "Calcul des tarifs exclusifs Forfeo...",
  "Finalisation de votre séjour sur mesure !"
];

// --- SUGGESTIONS DE RECHERCHE ---
const SUGGESTIONS = [
  { icon: <FaHeart className="text-pink-400" />, text: "Week-end romantique dans Charlevoix avec spa et resto chic, budget 500$." },
  { icon: <FaLeaf className="text-green-400" />, text: "Escapade nature près de Québec pour 2, on veut bouger et bien manger." },
  { icon: <FaWineGlassAlt className="text-purple-400" />, text: "Découverte gastronomique, je veux les meilleures tables et un bel hôtel." }
];

// --- FAQ ---
const FAQS = [
  { 
    q: "Comment l'IA choisit-elle les lieux ?", 
    a: "Notre Intelligence Artificielle est connectée directement à l'inventaire en temps réel de nos partenaires Forfeo locaux. Elle filtre, compare et sélectionne les meilleures options selon vos critères."
  },
  { 
    q: "Est-ce que je paye un supplément pour ce service ?", 
    a: "Absolument pas ! Le Générateur IA est 100% gratuit. Mieux encore, regrouper vos réservations dans un 'Pack IA' vous donne souvent accès à des tarifs exclusifs."
  },
  { 
    q: "Puis-je modifier l'itinéraire généré ?", 
    a: "Bien sûr. Si une suggestion ne vous plaît pas, vous pouvez simplement relancer une génération en précisant ce que vous souhaitez changer dans la barre de recherche."
  }
];

export default function AiTripBuilder() {
  const navigate = useNavigate();
  const { addToCart, setIsCartOpen } = useCart();

  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiStepIndex, setAiStepIndex] = useState(0);
  const [itinerary, setItinerary] = useState<TripItem[] | null>(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // --- VRAI APPEL VERS L'IA ---
  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    
    setItinerary(null);
    setAiStepIndex(0);
    setIsGenerating(true);

    const animationInterval = setInterval(() => {
      setAiStepIndex((prev) => (prev + 1) % AI_STEPS.length);
    }, 1500);

    try {
      const { data, error } = await supabase.functions.invoke('generate-trip', {
        body: { prompt }
      });

      if (error) throw error;
      
      let parsedData = data;
      if (data && data.itinerary) {
          parsedData = data.itinerary;
      } else if (typeof data === 'string') {
          parsedData = JSON.parse(data);
          if (parsedData.itinerary) parsedData = parsedData.itinerary;
      }

      const realItinerary = parsedData.map((item: any) => ({
        ...item,
        icon: item.type === 'hotel' ? <FaHotel className="text-blue-500" /> : 
              item.type === 'resto' ? <FaUtensils className="text-orange-500" /> : 
              <FaSpa className="text-emerald-500" />
      }));

      setItinerary(realItinerary);

    } catch (err: any) {
      console.error(err);
      alert("Une erreur est survenue lors de la génération de l'itinéraire. " + (err.message || ""));
    } finally {
      clearInterval(animationInterval);
      setIsGenerating(false);
    }
  };

  // --- AJOUT AU PANIER SÉCURISÉ (AUTO-HEALING) ---
  const handleAddAllToCart = async () => {
    setIsAddingToCart(true);
    let hasError = false;

    // On utilise une boucle for...of pour pouvoir faire des appels asynchrones (await)
    for (const item of itinerary || []) {
      let safeBusinessId = item.business_id || (item as any).businessId;

      // 🛡️ AUTO-GUÉRISON : Si l'IA a oublié de nous donner le business_id ou a inventé un faux texte
      if (!safeBusinessId || safeBusinessId === "ID_DU_BUSINESS") {
        console.log(`Recherche du partenaire manquant pour l'article: ${item.title}...`);
        
        try {
          if (item.type === 'hotel') {
            const { data } = await supabase.from('accommodations').select('business_id').eq('id', item.id).maybeSingle();
            if (data?.business_id) safeBusinessId = data.business_id;
          } else {
            const { data } = await supabase.from('boutique_forfaits').select('business_id').eq('id', item.id).maybeSingle();
            if (data?.business_id) safeBusinessId = data.business_id;
          }
        } catch (e) {
          console.error("Erreur de récupération du partenaire", e);
        }
      }

      // Si après l'auto-guérison on n'a toujours rien, on bloque cet article spécifique
      if (!safeBusinessId || safeBusinessId === "ID_DU_BUSINESS") {
        console.error("Article orphelin bloqué :", item);
        alert(`Attention: L'article "${item.title}" n'est lié à aucun partenaire valide. Il ne sera pas ajouté pour éviter une erreur de paiement.`);
        hasError = true;
        continue; // On passe à l'article suivant
      }

      // Ajout au panier
      addToCart({
        id: item.id,
        source_table: 'boutique_forfaits',
        source_id: item.id,
        type: 'boutique_forfait', 
        title: `Pack IA - ${item.title}`,
        price: item.price,
        quantity: 1,
        image: item.image,
        business_id: safeBusinessId // Le VRAI id est maintenant garanti
      });
    }

    setIsAddingToCart(false);

    // On ouvre le panier et on redirige (même si un des articles a échoué, on envoie les bons)
    setTimeout(() => {
      setIsCartOpen(true);
      navigate('/checkout');
    }, 400);
  };

  const totalPrice = itinerary?.reduce((sum, item) => sum + item.price, 0) || 0;

  return (
    <div className="min-h-screen bg-[#0B0F19] font-sans text-white overflow-hidden">
      <Header />

      {/* BACKGROUND MAGIQUE */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40rem] h-[40rem] bg-purple-600 rounded-full mix-blend-screen filter blur-[150px] opacity-20 animate-pulse-slow"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40rem] h-[40rem] bg-cyan-400 rounded-full mix-blend-screen filter blur-[150px] opacity-20" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10 pt-28 pb-24 px-4 max-w-4xl mx-auto">
        
        {/* EN-TÊTE */}
        <div className="text-center mb-12 animate-in slide-in-from-top-8 duration-700">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-500/30 px-5 py-2 rounded-full text-xs font-black uppercase tracking-widest text-cyan-300 mb-6 backdrop-blur-md shadow-[0_0_20px_rgba(168,85,247,0.2)]">
            <FaMagic className="animate-pulse" /> Forfeo IA Générative
          </div>
          <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight tracking-tighter">
            Votre séjour parfait, <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500">
              créé en un clic.
            </span>
          </h1>
          <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto font-medium">
            Décrivez vos envies, votre budget et laissez notre intelligence artificielle concevoir un itinéraire sur mesure avec nos meilleurs partenaires locaux.
          </p>
        </div>

        {/* BARRE DE RECHERCHE IA */}
        {!itinerary && !isGenerating && (
          <div className="animate-in zoom-in-95 duration-500 delay-100">
            <div className="bg-white/5 backdrop-blur-2xl p-4 md:p-6 rounded-[2.5rem] border border-white/10 shadow-2xl relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-[2.6rem] blur opacity-20"></div>
              <form onSubmit={handleGenerate} className="relative z-10">
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Ex: Un week-end romantique dans Charlevoix pour deux, budget de 600$, on adore les spas et la gastronomie locale..."
                  className="w-full bg-black/40 border border-white/10 rounded-[1.5rem] p-6 text-white text-lg placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all resize-none min-h-[140px]"
                  required
                />
                <div className="absolute bottom-4 right-4 flex items-center gap-4">
                  <span className="text-xs text-slate-500 font-medium hidden md:block">
                    Appuyez sur <kbd className="bg-white/10 px-2 py-1 rounded font-mono text-cyan-400">Générer</kbd>
                  </span>
                  <button 
                    type="submit"
                    disabled={!prompt.trim()}
                    className="bg-gradient-to-r from-cyan-500 to-purple-600 text-white px-8 py-3.5 rounded-xl font-black uppercase tracking-widest hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] hover:scale-105 transition-all disabled:opacity-50 disabled:hover:scale-100 flex items-center gap-2"
                  >
                    Générer mon voyage <FaArrowRight />
                  </button>
                </div>
              </form>
            </div>

            {/* SUGGESTIONS RAPIDES */}
            <div className="mt-8">
              <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 pl-2">Manque d'inspiration ? Essayez :</p>
              <div className="flex flex-wrap gap-3">
                {SUGGESTIONS.map((sug, i) => (
                  <button
                    key={i}
                    onClick={() => setPrompt(sug.text)}
                    className="flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-cyan-500/50 text-slate-300 px-4 py-2.5 rounded-xl text-sm font-medium transition-all text-left"
                  >
                    {sug.icon} {sug.text.substring(0, 40)}...
                  </button>
                ))}
              </div>
            </div>

            {/* BADGES DE CONFIANCE */}
            <div className="mt-12 flex flex-wrap justify-center gap-6 md:gap-12 text-slate-400">
              <div className="flex items-center gap-2 text-sm font-bold">
                <FaShieldAlt className="text-cyan-500 text-xl" />
                <span>Partenaires Locaux Vérifiés</span>
              </div>
              <div className="flex items-center gap-2 text-sm font-bold">
                <FaCheckCircle className="text-purple-500 text-xl" />
                <span>Disponibilités en temps réel</span>
              </div>
            </div>

            {/* FAQ SECTION */}
            <div className="mt-20 border-t border-white/10 pt-16 max-w-3xl mx-auto">
              <h3 className="text-2xl font-black text-center mb-8">Questions Fréquentes</h3>
              <div className="space-y-4">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden transition-all">
                    <button 
                      onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                      className="w-full px-6 py-4 flex items-center justify-between font-bold text-left hover:bg-white/5 transition"
                    >
                      {faq.q}
                      <FaChevronDown className={`transition-transform ${openFaqIndex === i ? 'rotate-180 text-cyan-400' : 'text-slate-500'}`} />
                    </button>
                    {openFaqIndex === i && (
                      <div className="px-6 pb-4 text-slate-400 text-sm leading-relaxed">
                        {faq.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ÉTAT DE CHARGEMENT DE L'IA */}
        {isGenerating && (
          <div className="flex flex-col items-center justify-center py-32 animate-in fade-in duration-500">
            <div className="relative">
              <div className="absolute inset-0 bg-cyan-500 blur-[50px] opacity-40 rounded-full animate-pulse-slow"></div>
              <div className="w-28 h-28 bg-gradient-to-tr from-cyan-500 to-purple-600 rounded-full flex items-center justify-center mb-8 relative z-10 animate-spin-slow">
                <div className="w-24 h-24 bg-[#0B0F19] rounded-full flex items-center justify-center">
                  <FaMagic className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400 text-5xl animate-pulse" />
                </div>
              </div>
            </div>
            <h2 className="text-3xl font-black text-white mb-3">L'IA prépare votre voyage...</h2>
            <div className="bg-white/10 px-6 py-2 rounded-full border border-white/20">
              <p className="text-cyan-400 font-mono tracking-widest uppercase text-sm">
                {AI_STEPS[aiStepIndex]}
              </p>
            </div>
          </div>
        )}

        {/* RÉSULTAT : L'ITINÉRAIRE */}
        {itinerary && !isGenerating && (
          <div className="space-y-8 animate-in slide-in-from-bottom-10 duration-700">
            
            <div className="flex flex-col md:flex-row justify-between items-center bg-white/5 backdrop-blur-xl p-6 rounded-3xl border border-white/10 shadow-xl">
              <div>
                <h2 className="text-2xl font-black text-white flex items-center gap-3">
                  <FaStar className="text-yellow-400" /> Itinéraire Recommandé
                </h2>
                <p className="text-slate-400 text-sm mt-1">Basé sur vos préférences et les meilleures disponibilités de nos partenaires.</p>
              </div>
              <button 
                onClick={() => setItinerary(null)} 
                className="mt-4 md:mt-0 px-5 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-bold transition-all border border-white/10"
              >
                Refaire une demande
              </button>
            </div>

            {/* Timeline de l'itinéraire */}
            <div className="relative border-l-2 border-white/10 pl-6 md:pl-8 ml-4 md:ml-6 space-y-12 py-4">
              {itinerary.map((item, index) => {
                
                // Préparation du lien cliquable (Nouvel onglet)
                const safeBusinessId = item.business_id || (item as any).businessId;
                const targetUrl = safeBusinessId && safeBusinessId !== "ID_DU_BUSINESS" ? `/place/${safeBusinessId}` : '#';

                return (
                  <div key={item.id} className="relative group">
                    {/* Point sur la timeline */}
                    <div className="absolute -left-[41px] md:-left-[49px] top-4 w-8 h-8 rounded-full bg-[#0B0F19] border-2 border-slate-700 flex items-center justify-center group-hover:border-cyan-400 group-hover:bg-cyan-900 group-hover:scale-125 transition-all duration-300 z-10 shadow-[0_0_15px_rgba(0,0,0,0.5)]">
                      {item.icon}
                    </div>

                    {/* Carte d'activité */}
                    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-[2rem] p-6 hover:bg-white/10 hover:border-white/20 hover:shadow-[0_0_30px_rgba(6,182,212,0.1)] transition-all duration-300 flex flex-col md:flex-row gap-6">
                      
                      {/* IMAGE CLICABLE (Ouvre un nouvel onglet) */}
                      <a 
                        href={targetUrl} 
                        target={targetUrl !== '#' ? "_blank" : "_self"} 
                        rel="noreferrer"
                        className="w-full md:w-56 h-48 rounded-2xl overflow-hidden shrink-0 relative block group/link"
                      >
                        <img src={item.image || "https://placehold.co/800x800?text=Forfeo"} alt={item.title} className="w-full h-full object-cover group-hover/link:scale-110 transition duration-700" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-md px-3 py-1 rounded-lg text-xs font-black uppercase tracking-widest flex items-center gap-2">
                          <FaMapMarkerAlt className="text-cyan-400" /> Confirmé
                        </div>
                        {targetUrl !== '#' && (
                          <div className="absolute inset-0 bg-cyan-500/20 opacity-0 group-hover/link:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-sm">
                            <span className="bg-slate-900 text-white px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 shadow-lg">Visiter <FaArrowRight /></span>
                          </div>
                        )}
                      </a>

                      <div className="flex-1 flex flex-col justify-center">
                        <p className="text-cyan-400 font-black text-xs uppercase tracking-widest mb-2 flex items-center gap-2">
                          <FaCalendarDay /> {item.time}
                        </p>
                        
                        {/* TITRE CLICABLE */}
                        <a 
                          href={targetUrl} 
                          target={targetUrl !== '#' ? "_blank" : "_self"} 
                          rel="noreferrer" 
                          className="hover:text-cyan-400 transition-colors inline-block w-fit"
                        >
                          <h3 className="text-2xl font-black text-white mb-3 flex items-center gap-3">
                            {item.title}
                            {targetUrl !== '#' && <FaExternalLinkAlt className="text-sm opacity-50" />}
                          </h3>
                        </a>

                        <p className="text-slate-400 leading-relaxed text-sm mb-4">
                          {item.description}
                        </p>
                        <div className="mt-auto inline-flex items-center gap-3">
                          <span className="bg-cyan-500/20 text-cyan-300 px-4 py-2 rounded-xl font-black text-lg border border-cyan-500/30">
                            {item.price ? item.price.toFixed(2) : "0.00"}$
                          </span>
                          <span className="text-xs text-slate-500 uppercase font-bold tracking-widest">/ pers.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* BARRE D'ACTION COLLANTE (Sticky Bottom) */}
            <div className="sticky bottom-8 mt-12 bg-gradient-to-r from-slate-900 to-[#0B0F19] p-6 rounded-[2.5rem] border border-white/20 shadow-[0_0_50px_rgba(0,0,0,0.8)] flex flex-col md:flex-row items-center justify-between gap-6 backdrop-blur-3xl z-50">
              <div>
                <p className="text-slate-400 text-sm font-bold uppercase tracking-widest mb-1">Total du séjour IA</p>
                <div className="flex items-end gap-3">
                  <p className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">{totalPrice.toFixed(2)}$</p>
                  <p className="text-sm font-medium text-slate-500 line-through mb-1">{(totalPrice * 1.15).toFixed(2)}$</p>
                </div>
                <p className="text-cyan-400 text-xs font-bold mt-2 flex items-center gap-1"><FaCheckCircle /> Pack tout-en-un optimisé</p>
              </div>
              <button 
                onClick={handleAddAllToCart}
                disabled={isAddingToCart}
                className="w-full md:w-auto bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-10 py-5 rounded-2xl font-black uppercase tracking-widest hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] hover:scale-[1.02] transition-all flex items-center justify-center gap-3 disabled:opacity-70 disabled:hover:scale-100"
              >
                {isAddingToCart ? <FaSync className="animate-spin" /> : <FaShoppingCart className="text-xl" />}
                {isAddingToCart ? 'Réservation...' : 'Réserver ce séjour'}
              </button>
            </div>

          </div>
        )}
      </div>

      <Footer />

      <style dangerouslySetInnerHTML={{__html: `
        .animate-spin-slow { animation: spin 8s linear infinite; }
        .animate-pulse-slow { animation: pulse 6s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      `}} />
    </div>
  );
}
